import type { PermissionsMap } from "@printflow/auth-shared";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Etiqueta, Interruptor } from "../../components/ui/primitivos";
import type { Permiso } from "../../lib/consola-api";

/**
 * Editor de permisos de un rol.
 *
 * Porteado de `RoleDetailPage` de `imprenta`: grupos por recurso, colapsables,
 * con el contador activos/total y un interruptor por permiso.
 *
 * Es el componente donde un error se paga mas caro de toda la consola —tildar
 * mal un permiso es dar acceso que nadie pidio— asi que la conversion entre el
 * mapa `recurso -> acciones` que viaja por la API y el estado de la pantalla
 * vive en funciones puras, testeadas aparte.
 */

/**
 * Etiquetas de recurso, porteadas de `imprenta` — incluida la que importa:
 * **`presupuestos` se muestra como «Consultas»**.
 *
 * Acá pesa más que allá, porque es el nombre que usan el ítem del menú y el
 * título de la pantalla. Quien va a buscar los permisos de Consultas no
 * encuentra un grupo llamado «Presupuestos».
 */
const NOMBRE_RECURSO: Record<string, string> = {
  presupuestos: "Consultas",
  clientes: "Clientes",
  produccion: "Producción",
  impresoras: "Impresoras",
  usuarios: "Usuarios",
  roles: "Roles",
  catalogos: "Catálogos",
  productos: "Productos",
  precios: "Lista de Precios",
  facturacion: "Facturación",
  auditoria: "Auditoría",
  reportes: "Reportes",
  sistema: "Sistema",
};

/**
 * El nombre visible de un recurso. Sin etiqueta devuelve el código tal cual:
 * el catálogo puede crecer sin que este mapa lo siga, y un grupo sin nombre es
 * un desplegable del que no se sabe qué contiene.
 */
export function nombreDeRecurso(recurso: string): string {
  return NOMBRE_RECURSO[recurso] ?? recurso;
}

/** `{ produccion: ["read"] }` -> `Set { "produccion.read" }`. */
export function aCodigos(permisos: PermissionsMap): Set<string> {
  const codigos = new Set<string>();
  for (const [recurso, acciones] of Object.entries(permisos)) {
    for (const accion of acciones) codigos.add(`${recurso}.${accion}`);
  }
  return codigos;
}

/**
 * `Set { "produccion.read" }` -> `{ produccion: ["read"] }`.
 *
 * Parte por el PRIMER punto: hay codigos de tres segmentos
 * (`roles.permisos.read`, `sistema.config.update`) y partir por el ultimo los
 * mandaria a un recurso inexistente, que el worker rechazaria como fuera del
 * catalogo.
 */
export function aMapa(codigos: ReadonlySet<string>): PermissionsMap {
  const mapa: PermissionsMap = {};
  for (const codigo of codigos) {
    const corte = codigo.indexOf(".");
    if (corte <= 0) continue;
    const recurso = codigo.slice(0, corte);
    const accion = codigo.slice(corte + 1);
    const acciones = mapa[recurso] ?? [];
    acciones.push(accion);
    mapa[recurso] = acciones;
  }
  return mapa;
}

export function agruparPorRecurso(catalogo: readonly Permiso[]): Map<string, Permiso[]> {
  const grupos = new Map<string, Permiso[]>();
  for (const permiso of catalogo) {
    const grupo = grupos.get(permiso.recurso);
    if (grupo) grupo.push(permiso);
    else grupos.set(permiso.recurso, [permiso]);
  }
  return grupos;
}

export function EditorPermisos({
  catalogo,
  seleccionados,
  onCambiar,
  soloLectura,
}: {
  catalogo: readonly Permiso[];
  seleccionados: ReadonlySet<string>;
  onCambiar: (codigo: string, activo: boolean) => void;
  soloLectura: boolean;
}) {
  const grupos = useMemo(() => agruparPorRecurso(catalogo), [catalogo]);
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>({});

  return (
    <div className="space-y-2">
      {[...grupos].map(([recurso, permisos]) => {
        const activos = permisos.filter((p) => seleccionados.has(p.codigo)).length;
        const abierto = abiertos[recurso] ?? false;
        return (
          <Collapsible.Root
            key={recurso}
            open={abierto}
            onOpenChange={(v) => setAbiertos((prev) => ({ ...prev, [recurso]: v }))}
          >
            <Collapsible.Trigger className="flex w-full items-center justify-between rounded-md bg-white px-3 py-2 text-left hover:bg-neutral-200">
              <span className="flex items-center gap-2 font-medium text-sm uppercase tracking-wide">
                {/* El chevron es lo único que dice que el grupo se abre: un
                    encabezado con un contador al lado se lee como un dato. */}
                {abierto ? (
                  <ChevronDown aria-hidden="true" className="h-4 w-4 text-neutral-500" />
                ) : (
                  <ChevronRight aria-hidden="true" className="h-4 w-4 text-neutral-500" />
                )}
                {nombreDeRecurso(recurso)}
              </span>
              <Etiqueta tono={activos > 0 ? "ok" : "neutral"}>
                {activos} / {permisos.length}
              </Etiqueta>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <ul className="mt-1 ml-4 space-y-1">
                {permisos.map((permiso) => (
                  <li
                    key={permiso.codigo}
                    className="flex items-center justify-between rounded-md bg-white/60 px-3 py-1.5"
                  >
                    <div>
                      <p className="text-neutral-800 text-sm">{permiso.descripcion}</p>
                      <p className="font-mono text-neutral-500 text-xs">{permiso.codigo}</p>
                    </div>
                    <Interruptor
                      aria-label={permiso.descripcion}
                      checked={seleccionados.has(permiso.codigo)}
                      disabled={soloLectura}
                      onCheckedChange={(v) => onCambiar(permiso.codigo, v)}
                    />
                  </li>
                ))}
              </ul>
            </Collapsible.Content>
          </Collapsible.Root>
        );
      })}
    </div>
  );
}
